import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef,
} from 'react';
import { api as http } from '../lib/api.ts';
import {
  caretOffset, isEmpty, onEdgeLine, placeCaret, plainOf, renderInto, spansFrom,
  splitSpans, textLength, toggleMark,
} from './rich.tsx';
import {
  applyBlocks, clearSelection, getState, newId, patchBlock, reloadDoc, saveBlockText, sendOps,
  setCaret, setLocalBlocks, setSelection, useStore, type Op,
} from './store.ts';
import type { Block, BlockType, Span } from './types.ts';

/**
 * The block being typed in, and where in it. Module-level on purpose: the
 * component that recorded it is gone by the time the replacement reads it.
 */
let held: { id: string; offset: number } | null = null;

/** Block types whose body is rich text the caret can sit in. */
export const TEXTUAL = new Set<BlockType>([
  'text', 'h1', 'h2', 'h3', 'bulleted', 'numbered', 'todo', 'toggle', 'quote', 'callout', 'code',
]);

/** Pressing Enter at the end of one of these starts a plain paragraph. */
const CONTINUES = new Set<BlockType>(['bulleted', 'numbered', 'todo']);

/** Types an empty Backspace turns back into a paragraph instead of merging. */
const RESETS = new Set<BlockType>(['h1', 'h2', 'h3', 'bulleted', 'numbered', 'todo', 'toggle', 'quote', 'callout', 'code']);

export type EditorApi = {
  blocks: Block[];
  /** Document order, as the eye reads it: parents before their children. */
  flat: Block[];
  update: (id: string, patch: Partial<Block>, send?: boolean) => void;
  setType: (id: string, type: BlockType, props?: Record<string, any>) => void;
  splitAt: (block: Block, offset: number) => void;
  backspaceAt: (block: Block) => void;
  deleteAt: (block: Block) => void;
  indent: (block: Block) => void;
  outdent: (block: Block) => void;
  moveCaret: (block: Block, dir: -1 | 1, at?: 'start' | 'end') => void;
  insertAfter: (block: Block | null, type: BlockType, props?: Record<string, any>) => string;
  remove: (block: Block) => void;
  /** Moves a whole selection, keeping the order the blocks were in. */
  moveMany: (ids: string[], parentId: string | null, afterId: string | null) => void;
  removeMany: (ids: string[]) => void;
  openSlash: (block: Block, rect: DOMRect) => void;
};

export const EditorCtx = createContext<EditorApi | null>(null);
export const useEditor = (): EditorApi => {
  const api = useContext(EditorCtx);
  if (!api) throw new Error('useEditor outside an editor');
  return api;
};

/** Depth-first order, which is what the arrow keys and Backspace follow. */
export function flatten(blocks: Block[], parentId: string | null = null, out: Block[] = []): Block[] {
  for (const block of blocks.filter((b) => b.parent_id === parentId).sort((a, b) => a.position - b.position)) {
    out.push(block);
    flatten(blocks, block.id, out);
  }
  return out;
}

export function childrenOf(blocks: Block[], parentId: string | null): Block[] {
  return blocks.filter((b) => b.parent_id === parentId).sort((a, b) => a.position - b.position);
}

/** A position between two siblings, matching what the server would pick. */
export function positionAfter(blocks: Block[], parentId: string | null, afterId: string | null): number {
  const siblings = childrenOf(blocks, parentId);
  if (!afterId) return (siblings.at(-1)?.position ?? 0) + 1024;
  const index = siblings.findIndex((b) => b.id === afterId);
  if (index < 0) return (siblings.at(-1)?.position ?? 0) + 1024;
  const here = siblings[index]!.position;
  const next = siblings[index + 1]?.position;
  return next === undefined ? here + 1024 : (here + next) / 2;
}

// ---- the editable line ----------------------------------------------------

export function RichText({
  block, placeholder, className, multiline,
}: {
  block: Block;
  placeholder?: string;
  className?: string;
  /** Code blocks keep Enter to themselves. */
  multiline?: boolean;
}) {
  const api = useEditor();
  const ref = useRef<HTMLDivElement | null>(null);
  // What the DOM currently shows, so an incoming render does not fight typing.
  const shown = useRef<string>('');
  const composing = useRef(false);

  const serialize = useCallback(() => {
    const el = ref.current;
    if (!el) return [] as Span[];
    const spans = spansFrom(el);
    shown.current = JSON.stringify(spans);
    return spans;
  }, []);

  // Paint the stored value in, but only when it differs from what is on screen.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = JSON.stringify(block.rich);
    if (next === shown.current) return;
    const keep = document.activeElement === el ? caretOffset(el) : null;
    renderInto(el, block.rich);
    shown.current = next;
    if (keep != null) placeCaret(el, keep);
  }, [block.rich]);

  // The caret the last structural change asked for.
  const caret = useStore((s) => s.caret);
  const wanted = caret && caret.blockId === block.id ? caret.at : null;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || wanted == null) return;
    el.focus({ preventScroll: true });
    placeCaret(el, wanted);
    setCaret(null);
    el.scrollIntoView({ block: 'nearest' });
  }, [wanted]);

  /**
   * Indenting a block moves it to another branch of the tree, so React
   * replaces its node and the browser drops the caret on the floor. If this
   * block was the one being typed in, take the caret back.
   */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || held?.id !== block.id) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    el.focus({ preventScroll: true });
    placeCaret(el, held.offset);
  }, []);

  const remember = () => {
    const el = ref.current;
    if (!el) return;
    held = { id: block.id, offset: caretOffset(el) ?? textLength(el) };
  };

  const onInput = () => {
    if (composing.current) return;
    const el = ref.current;
    if (!el) return;

    const spans = spansFrom(el);
    const text = plainOf(spans);
    // With no range of its own the caret is wherever the text ends; reading it
    // as 0 would quietly disable every shortcut on the line.
    const offset = caretOffset(el) ?? text.length;

    // Markdown as you type: the shortcut fires on the space that completes it.
    const shortcut = !multiline && matchShortcut(text);
    if (shortcut) {
      const [, rest] = splitSpans(spans, shortcut.consume);
      const props = { ...block.props, ...(shortcut.props ?? {}) };
      // `shown` is left as it was on purpose: it must differ from the trimmed
      // text so the paint effect repaints and the `# ` disappears.
      api.update(block.id, { type: shortcut.type, props, rich: rest }, true);
      // The caret keeps its place in the words, which is only the start when
      // the prefix was all there was. Sending it to 0 regardless would put it
      // in front of text the person had already typed, and the next Enter
      // would then split the line before its own contents.
      setCaret({ blockId: block.id, at: Math.max(0, offset - shortcut.consume) });
      return;
    }

    // Deleting the last character can leave a <br>, which would stop the
    // placeholder showing; clear the node so `:empty` stays true.
    if (!text && el.childNodes.length) el.replaceChildren();

    shown.current = JSON.stringify(spans);
    saveBlockText(block.id, spans);

    // `/` on its own opens the block menu.
    if (!multiline && text[offset - 1] === '/' && (offset === 1 || /\s/.test(text[offset - 2] ?? ''))) {
      const sel = window.getSelection();
      if (sel?.rangeCount) api.openSlash(block, sel.getRangeAt(0).getBoundingClientRect());
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || composing.current) return;
    const spans = spansFrom(el);
    const length = textLength(el);
    // When the caret cannot be read, the keys that need a position assume the
    // end, and the keys that only fire at an edge stand down. Reading an
    // unknown caret as 0 would make every Enter push the whole line into the
    // block below it.
    const known = caretOffset(el);
    const offset = known ?? length;
    const atStart = known === 0;
    const atEnd = known === length;
    const mod = e.metaKey || e.ctrlKey;

    if (mod && !e.altKey) {
      const mark = ({ b: 'b', i: 'i', u: 'u', e: 'c' } as const)[e.key.toLowerCase()];
      if (mark) {
        e.preventDefault();
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) return;
        const range = sel.getRangeAt(0);
        const start = offsetOf(el, range.startContainer, range.startOffset);
        const end = offsetOf(el, range.endContainer, range.endOffset);
        api.update(block.id, { rich: toggleMark(spans, Math.min(start, end), Math.max(start, end), mark) }, true);
        return;
      }
    }

    if (e.key === 'Enter') {
      if (multiline && !e.shiftKey) return;        // code blocks take their own
      if (e.shiftKey) {
        e.preventDefault();
        document.execCommand('insertText', false, '\n');
        onInput();
        return;
      }
      e.preventDefault();
      saveBlockText(block.id, spans);
      api.splitAt({ ...block, rich: spans }, offset);
      return;
    }

    if (e.key === 'Backspace' && atStart && window.getSelection()?.isCollapsed) {
      e.preventDefault();
      api.backspaceAt({ ...block, rich: spans });
      return;
    }

    if (e.key === 'Delete' && atEnd && window.getSelection()?.isCollapsed) {
      e.preventDefault();
      api.deleteAt({ ...block, rich: spans });
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      saveBlockText(block.id, spans);
      if (e.shiftKey) api.outdent(block);
      else api.indent(block);
      return;
    }

    if (e.key === 'ArrowUp' && onEdgeLine(el, 'first')) {
      e.preventDefault();
      api.moveCaret(block, -1, 'end');
      return;
    }
    if (e.key === 'ArrowDown' && onEdgeLine(el, 'last')) {
      e.preventDefault();
      api.moveCaret(block, 1, 'start');
      return;
    }
    if (e.key === 'ArrowLeft' && atStart) {
      e.preventDefault();
      api.moveCaret(block, -1, 'end');
      return;
    }
    if (e.key === 'ArrowRight' && atEnd) {
      e.preventDefault();
      api.moveCaret(block, 1, 'start');
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    // Several lines become several blocks; one line is just text.
    if (!multiline && /\n/.test(text.trim())) {
      void pasteAsBlocks(api, block, text);
      return;
    }
    document.execCommand('insertText', false, text);
    onInput();
  };

  return (
    <div
      ref={ref}
      className={`rich ${className ?? ''}`}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      role="textbox"
      aria-multiline={multiline ? 'true' : 'false'}
      data-placeholder={placeholder ?? ''}
      data-block={block.id}
      onInput={() => { onInput(); remember(); }}
      onKeyDown={onKeyDown}
      onKeyUp={remember}
      onPaste={onPaste}
      onFocus={remember}
      onMouseUp={remember}
      onBlur={() => { serialize(); }}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={() => { composing.current = false; onInput(); }}
    />
  );
}

function offsetOf(root: HTMLElement, node: Node, nodeOffset: number): number {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(node, nodeOffset);
  const frag = range.cloneContents();
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n = 0;
  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) n += (current.nodeValue ?? '').length;
    else if ((current as HTMLElement).tagName === 'BR') n += 1;
    current = walker.nextNode();
  }
  return n;
}

// ---- markdown shortcuts ---------------------------------------------------

type Shortcut = { type: BlockType; consume: number; props?: Record<string, any> };

/**
 * Markdown as you type.
 *
 * Each pattern matches a prefix and the rest of the line is kept, so a
 * shortcut converts whether it arrives one keystroke at a time or all at once
 * from a paste. Longer prefixes come first, so `>> ` is never read as a quote
 * and `## ` is never read as `# `.
 */
const SHORTCUTS: { re: RegExp; type: BlockType; props?: Record<string, any> }[] = [
  { re: /^(### )/, type: 'h3' },
  { re: /^(## )/, type: 'h2' },
  { re: /^(# )/, type: 'h1' },
  { re: /^(>> )/, type: 'toggle', props: { open: true } },
  { re: /^(\[[ xX]?\] )/, type: 'todo', props: { checked: false } },
  { re: /^([-*+] )/, type: 'bulleted' },
  { re: /^(\d{1,3}[.)] )/, type: 'numbered' },
  { re: /^(> )/, type: 'quote' },
  { re: /^(\| )/, type: 'callout', props: { icon: '\u{1F4A1}' } },
  { re: /^(```)/, type: 'code', props: { language: 'text' } },
  { re: /^(--- )/, type: 'divider' },
];

function matchShortcut(text: string): Shortcut | null {
  // A space at the end of a contenteditable is stored as U+00A0, so none of
  // these would match without normalising it first.
  const line = text.replace(/\u00a0/g, ' ');
  for (const { re, type, props } of SHORTCUTS) {
    const m = re.exec(line);
    if (m) return { type, consume: m[1]!.length, props };
  }
  return null;
}

// ---- multi-line paste -----------------------------------------------------

async function pasteAsBlocks(api: EditorApi, block: Block, text: string): Promise<void> {
  const pageId = getState().doc?.page.id;
  if (!pageId) return;
  try {
    const res = await http.post<{ blocks: Block[] }>('/api/blocks/import', {
      page_id: pageId, markdown: text, after: block.id,
    });
    setLocalBlocks(res.blocks);
    if (isEmpty(block.rich)) api.remove(block);
  } catch {
    document.execCommand('insertText', false, text);
  }
}

// ---- the operations the keys call ----------------------------------------

export function makeEditorApi(
  blocks: Block[],
  openSlash: (block: Block, rect: DOMRect) => void,
): EditorApi {
  const flat = flatten(blocks);
  const byId = new Map(blocks.map((b) => [b.id, b]));

  // What to do is decided from the list this render drew; the change itself
  // is applied to whatever the store holds by the time it lands.
  const commit = (fn: (current: Block[]) => Block[], ops: Op[]) => {
    applyBlocks(fn);
    void sendOps(ops);
  };

  const update: EditorApi['update'] = (id, patch, send = false) => {
    patchBlock(id, patch);
    if (send) {
      const op: Op = { op: 'update', id };
      if (patch.rich) op.rich = patch.rich;
      if (patch.type) op.type = patch.type;
      if (patch.props) op.props = patch.props;
      void sendOps([op]);
    }
  };

  const setType: EditorApi['setType'] = (id, type, props) => {
    const block = byId.get(id);
    if (!block) return;
    const next = { ...block.props, ...(props ?? {}) };
    update(id, { type, props: next }, false);
    void sendOps([{ op: 'update', id, type, props: next }]);
  };

  const insertAfter: EditorApi['insertAfter'] = (block, type, props) => {
    const id = newId('N');
    const parentId = block?.parent_id ?? null;
    const fresh: Block = {
      id,
      page_id: getState().doc?.page.id ?? '',
      parent_id: parentId,
      type,
      rich: [],
      props: props ?? {},
      position: positionAfter(blocks, parentId, block?.id ?? null),
    };
    commit((cur) => [...cur, fresh], [{
      op: 'insert', id, type, rich: [], props: props ?? {},
      parent_id: parentId, after: block?.id ?? null,
    }]);
    setCaret({ blockId: id, at: 'start' });
    return id;
  };

  const splitAt: EditorApi['splitAt'] = (block, offset) => {
    const [before, after] = splitSpans(block.rich, offset);

    // Enter on an empty list item steps back out of the list.
    if (isEmpty(block.rich) && RESETS.has(block.type) && block.type !== 'code') {
      if (block.parent_id) outdent(block);
      else setType(block.id, 'text');
      setCaret({ blockId: block.id, at: 'start' });
      return;
    }

    const type: BlockType = CONTINUES.has(block.type) ? block.type : 'text';
    const props = block.type === 'todo' ? { checked: false } : {};
    const id = newId('N');

    // A split at the very start pushes the existing text down instead, so the
    // new empty line appears above and the caret stays with the words.
    const kids = childrenOf(blocks, block.id);
    const parentId = kids.length ? block.id : block.parent_id;
    const afterId = kids.length ? null : block.id;

    const fresh: Block = {
      id,
      page_id: block.page_id,
      parent_id: parentId,
      type,
      rich: after,
      props,
      position: kids.length
        ? (kids[0]!.position) / 2
        : positionAfter(blocks, block.parent_id, block.id),
    };

    commit(
      (cur) => [...cur.map((b) => (b.id === block.id ? { ...b, rich: before } : b)), fresh],
      [
        { op: 'update', id: block.id, rich: before },
        { op: 'insert', id, type, rich: after, props, parent_id: parentId, after: afterId },
      ],
    );
    setCaret({ blockId: id, at: 'start' });
  };

  const indent: EditorApi['indent'] = (block) => {
    const siblings = childrenOf(blocks, block.parent_id);
    const index = siblings.findIndex((b) => b.id === block.id);
    const previous = siblings[index - 1];
    if (!previous) return;
    const kids = childrenOf(blocks, previous.id);
    const position = (kids.at(-1)?.position ?? 0) + 1024;
    commit(
      (cur) => cur.map((b) => (b.id === block.id ? { ...b, parent_id: previous.id, position } : b)),
      [{ op: 'update', id: block.id, parent_id: previous.id, after: kids.at(-1)?.id ?? null }],
    );
    setCaret({ blockId: block.id, at: 'end' });
  };

  const outdent: EditorApi['outdent'] = (block) => {
    const parent = block.parent_id ? byId.get(block.parent_id) : undefined;
    if (!parent) return;
    const position = positionAfter(blocks, parent.parent_id, parent.id);
    commit(
      (cur) => cur.map((b) => (b.id === block.id ? { ...b, parent_id: parent.parent_id, position } : b)),
      [{ op: 'update', id: block.id, parent_id: parent.parent_id, after: parent.id }],
    );
    setCaret({ blockId: block.id, at: 'end' });
  };

  /**
   * The blocks of a selection that carry the rest.
   *
   * A block whose parent is also moving travels with it; sending both would
   * reparent the child out of the branch it is riding in.
   */
  const topMost = (ids: string[]): string[] => {
    const moving = new Set(ids);
    const tops = ids.filter((id) => {
      let parent = byId.get(id)?.parent_id;
      while (parent) {
        if (moving.has(parent)) return false;
        parent = byId.get(parent)?.parent_id;
      }
      return true;
    });
    // Document order, so they land in the order they were read in.
    return flat.filter((b) => tops.includes(b.id)).map((b) => b.id);
  };

  const moveMany: EditorApi['moveMany'] = (ids, parentId, afterId) => {
    const ordered = topMost(ids);
    if (!ordered.length) return;

    // Nothing may be dropped inside its own subtree.
    const moving = new Set(ordered);
    if (parentId) {
      let walk: string | null | undefined = parentId;
      while (walk) {
        if (moving.has(walk)) return;
        walk = byId.get(walk)?.parent_id;
      }
    }
    if (afterId && moving.has(afterId)) return;

    // No anchor means the drop was above everything else in this container,
    // so the first block goes to the front and the rest line up behind it.
    const ops: Op[] = [];
    let after = afterId;
    for (const id of ordered) {
      ops.push(after === null
        ? { op: 'update', id, parent_id: parentId, first: true }
        : { op: 'update', id, parent_id: parentId, after });
      after = id;
    }
    applyBlocks((cur) => cur.map((b) => (moving.has(b.id) ? { ...b, parent_id: parentId } : b)));
    void sendOps(ops).then(() => reloadDoc());
  };

  const removeMany: EditorApi['removeMany'] = (ids) => {
    const ordered = topMost(ids);
    if (!ordered.length) return;
    const gone = new Set(ordered);
    // A child of a removed block goes with it, so only the tops are sent.
    commit(
      (cur) => cur.filter((b) => {
        let walk: string | null | undefined = b.id;
        while (walk) {
          if (gone.has(walk)) return false;
          walk = cur.find((x) => x.id === walk)?.parent_id;
        }
        return true;
      }),
      ordered.map((id): Op => ({ op: 'delete', id })),
    );
  };

  const remove: EditorApi['remove'] = (block) => {
    commit((cur) => cur.filter((b) => b.id !== block.id), [{ op: 'delete', id: block.id }]);
  };

  const backspaceAt: EditorApi['backspaceAt'] = (block) => {
    // A styled empty line becomes a paragraph first; the second press merges.
    if (RESETS.has(block.type)) {
      setType(block.id, 'text');
      setCaret({ blockId: block.id, at: 'start' });
      return;
    }
    if (block.parent_id && isEmpty(block.rich)) { outdent(block); return; }

    const index = flat.findIndex((b) => b.id === block.id);
    const previous = flat[index - 1];
    if (!previous) return;

    if (!TEXTUAL.has(previous.type)) {
      // A divider or an image above just goes.
      commit((cur) => cur.filter((b) => b.id !== previous.id), [{ op: 'delete', id: previous.id }]);
      return;
    }

    const at = plainOf(previous.rich).length;
    const merged = [...previous.rich, ...block.rich];
    const orphans = childrenOf(blocks, block.id);

    commit(
      (cur) => cur
        .filter((b) => b.id !== block.id)
        .map((b) => {
          if (b.id === previous.id) return { ...b, rich: merged };
          if (b.parent_id === block.id) return { ...b, parent_id: previous.id };
          return b;
        }),
      [
        { op: 'update', id: previous.id, rich: merged },
        ...orphans.map((child): Op => ({ op: 'update', id: child.id, parent_id: previous.id })),
        { op: 'delete', id: block.id },
      ],
    );
    setCaret({ blockId: previous.id, at });
  };

  const deleteAt: EditorApi['deleteAt'] = (block) => {
    const index = flat.findIndex((b) => b.id === block.id);
    const next = flat[index + 1];
    if (!next) return;
    if (!TEXTUAL.has(next.type)) {
      commit((cur) => cur.filter((b) => b.id !== next.id), [{ op: 'delete', id: next.id }]);
      return;
    }
    const at = plainOf(block.rich).length;
    const merged = [...block.rich, ...next.rich];
    const orphans = childrenOf(blocks, next.id);
    commit(
      (cur) => cur
        .filter((b) => b.id !== next.id)
        .map((b) => {
          if (b.id === block.id) return { ...b, rich: merged };
          if (b.parent_id === next.id) return { ...b, parent_id: block.id };
          return b;
        }),
      [
        { op: 'update', id: block.id, rich: merged },
        ...orphans.map((child): Op => ({ op: 'update', id: child.id, parent_id: block.id })),
        { op: 'delete', id: next.id },
      ],
    );
    setCaret({ blockId: block.id, at });
  };

  const moveCaret: EditorApi['moveCaret'] = (block, dir, at = 'end') => {
    const index = flat.findIndex((b) => b.id === block.id);
    for (let i = index + dir; i >= 0 && i < flat.length; i += dir) {
      const candidate = flat[i]!;
      if (TEXTUAL.has(candidate.type)) {
        setCaret({ blockId: candidate.id, at });
        return;
      }
    }
  };

  return {
    blocks, flat, update, setType, splitAt, backspaceAt, deleteAt,
    indent, outdent, moveCaret, insertAfter, remove, moveMany, removeMany, openSlash,
  };
}

/** Clicking the empty space under a page starts a new paragraph there. */
export function useTrailingClick(api: EditorApi) {
  return useCallback((e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    const last = api.flat.at(-1);
    if (last && TEXTUAL.has(last.type) && isEmpty(last.rich)) {
      setCaret({ blockId: last.id, at: 'end' });
      return;
    }
    api.insertAfter(childrenOf(api.blocks, null).at(-1) ?? null, 'text');
  }, [api]);
}

/** Keeps the document from ever being completely uneditable. */
export function useEnsureTrailingBlock(api: EditorApi, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const top = childrenOf(api.blocks, null);
    if (!top.length) api.insertAfter(null, 'text');
  }, [api, enabled]);
}
